import { post } from './httpClient';
import {
  NL_SEARCH_MOCK_CASES,
  NL_SEARCH_SHARED_MOCK_CASES,
  NL_SEARCH_CAD_MOCK_CASES,
} from '../Map/data/nlSearchMockResponses';
import {
  NL_SEARCH_MIN_CHARS,
  NL_SEARCH_MAX_CHARS,
  NL_SEARCH_SCHEMA_VERSION,
  NL_SEARCH_SHARED_CONTEXT,
  NL_SEARCH_SHARED_SCHEMA_VERSION,
  NL_SEARCH_CAD_CONTEXT,
  NL_SEARCH_CAD_SCHEMA_VERSION,
  NL_SEARCH_TIMEOUT_MS,
} from '../Map/data/nlSearchConstants';
import { normalizeNlQuery } from '../Map/utils/nlSearchQuery';

const MOCK_DELAY_MS = 280;

/**
 * Parse de lenguaje natural. En localhost pega a `POST /parsesearchquery`
 * (API Gateway de dev). El diccionario local queda por si hay que trabajar offline.
 *
 * `REACT_APP_NL_SEARCH_ENABLED=false` apaga la feature (solo Nominatim).
 * `REACT_APP_NL_SEARCH_MOCK=true` usa el stub del front en vez del API.
 */
export const isNlSearchEnabled = () => process.env.REACT_APP_NL_SEARCH_ENABLED !== 'false';

export const isNlSearchMock = () => process.env.REACT_APP_NL_SEARCH_MOCK === 'true';

const MOCK_BY_QUERY = new Map(
  NL_SEARCH_MOCK_CASES.map((item) => [normalizeNlQuery(item.query), item.data]),
);

const SHARED_MOCK_BY_QUERY = new Map(
  NL_SEARCH_SHARED_MOCK_CASES.map((item) => [normalizeNlQuery(item.query), item.data]),
);

const CAD_MOCK_BY_QUERY = new Map(
  NL_SEARCH_CAD_MOCK_CASES.map((item) => [normalizeNlQuery(item.query), item.data]),
);

const delay = (ms, signal) => new Promise((resolve, reject) => {
  const timer = setTimeout(resolve, ms);
  const onAbort = () => {
    clearTimeout(timer);
    const error = new Error('Aborted');
    error.name = 'AbortError';
    reject(error);
  };
  if (signal?.aborted) {
    onAbort();
    return;
  }
  signal?.addEventListener('abort', onAbort, { once: true });
});

const unknownData = (query, schemaVersion = NL_SEARCH_SCHEMA_VERSION) => ({
  schema_version: schemaVersion,
  intent: 'unknown',
  filters: {},
  location: null,
  unresolved: [query.trim()],
  confidence: 1,
});

const CHEAP_RE = /\b(barato|baratos|economico|economicos)\b/;
const EXPENSIVE_RE = /\b(caro|caros|costoso|costosos)\b/;

/**
 * El mock no tiene LLM: barato/caro se resuelven con el primer/último corte
 * que mandó el front, igual que el sanitizer share.
 * @param {object} data
 * @param {string} query
 * @param {string[]} [listingPriceBands]
 * @param {string} [listingCurrency]
 * @returns {object}
 */
const applySharedMockPriceBands = (data, query, listingPriceBands, listingCurrency) => {
  const nq = normalizeNlQuery(query);
  const isCheap = CHEAP_RE.test(nq);
  const isExpensive = EXPENSIVE_RE.test(nq);
  if (!isCheap && !isExpensive) return data;

  const bands = Array.isArray(listingPriceBands) ? listingPriceBands.filter(Boolean) : [];
  if (!bands.length) {
    const unresolved = Array.isArray(data.unresolved) ? [...data.unresolved] : [];
    const echo = isCheap ? 'barato' : 'caro';
    if (!unresolved.some((item) => String(item).toLowerCase().includes(echo))) {
      unresolved.push(echo);
    }
    return { ...data, unresolved };
  }

  const priceKey = isExpensive ? bands[bands.length - 1] : bands[0];
  const filters = { ...(data.filters || {}), listing_price: [priceKey] };
  if (!filters.listing_currency) {
    const hint = String(listingCurrency || '').trim().toUpperCase();
    filters.listing_currency = [hint || 'USD'];
  }

  return {
    ...data,
    intent: data.intent === 'unknown' ? 'filter' : data.intent,
    filters,
    unresolved: (data.unresolved || []).filter((item) => (
      !CHEAP_RE.test(normalizeNlQuery(item)) && !EXPENSIVE_RE.test(normalizeNlQuery(item))
    )),
  };
};

/**
 * Stub local: mismos JSON que devolverá el diccionario de Lambda.
 * @param {string} query
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {string} [options.context]
 * @param {string} [options.listingCurrency]
 * @param {string[]} [options.listingPriceBands]
 * @returns {Promise<object>} Envelope `_httpOk` como `httpClient`.
 */
const parseSearchQueryMock = async (query, {
  signal,
  context,
  listingCurrency,
  listingPriceBands,
} = {}) => {
  await delay(MOCK_DELAY_MS, signal);
  const isShared = context === NL_SEARCH_SHARED_CONTEXT;
  const isCad = context === NL_SEARCH_CAD_CONTEXT;
  const data = isCad
    ? (CAD_MOCK_BY_QUERY.get(normalizeNlQuery(query))
      || unknownData(query, NL_SEARCH_CAD_SCHEMA_VERSION))
    : (isShared
      ? (SHARED_MOCK_BY_QUERY.get(normalizeNlQuery(query))
        || unknownData(query, NL_SEARCH_SHARED_SCHEMA_VERSION))
      : (MOCK_BY_QUERY.get(normalizeNlQuery(query)) || unknownData(query)));
  return {
    message: 'Consulta interpretada',
    data: isShared
      ? applySharedMockPriceBands(data, query, listingPriceBands, listingCurrency)
      : data,
    _httpOk: true,
    _httpStatus: 200,
    _httpHeaders: null,
  };
};

/**
 * Traduce una frase a filtros + lugar. No devuelve parcelas.
 * Sin `context` el body es solo Lote (`parcel-filters-v1`).
 * `context: 'shared-list'` pide Lote ∪ Publicación (`shared-list-v1`).
 * `context: 'cad-list'` pide ficha CAD (`cad-list-v1`), sin Nominatim.
 * En share, `listing_price_bands` desbloquea monto / barato / caro.
 * `view_province` desambigua barrios homónimos (solo si hay una provincia clara).
 * El mapa país no manda campos de listing.
 *
 * @param {string} query - Texto crudo del input.
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {string|null} [options.context]
 * @param {string} [options.listingCurrency] moneda del panel (`USD` | `ARS` | `EUR`).
 * @param {string[]} [options.listingPriceBands] keys `min:max` del chip de precio.
 * @param {string} [options.viewProvince] provincia visible (`Córdoba`, `CABA`, …).
 * @returns {Promise<object>} Envelope `{ message, data, _httpOk, _httpStatus }`.
 * @see docs/maps/natural-language-search.md
 */
export const parseSearchQuery = (query, {
  signal,
  context,
  listingCurrency,
  listingPriceBands,
  viewProvince,
} = {}) => {
  const trimmed = String(query || '').trim().slice(0, NL_SEARCH_MAX_CHARS);
  if (trimmed.length < NL_SEARCH_MIN_CHARS) {
    return Promise.resolve({
      message: 'Consulta demasiado corta',
      data: null,
      _httpOk: false,
      _httpStatus: 400,
      _httpHeaders: null,
    });
  }

  if (isNlSearchMock()) {
    return parseSearchQueryMock(trimmed, {
      signal,
      context,
      listingCurrency,
      listingPriceBands,
    });
  }

  const body = { query: trimmed, locale: 'es-AR' };
  const province = String(viewProvince || '').trim();
  if (province) body.view_province = province;
  if (context === NL_SEARCH_SHARED_CONTEXT) {
    body.context = NL_SEARCH_SHARED_CONTEXT;
    const currency = String(listingCurrency || '').trim().toUpperCase();
    if (currency) body.listing_currency = currency;
    if (Array.isArray(listingPriceBands) && listingPriceBands.length) {
      body.listing_price_bands = listingPriceBands;
    }
  } else if (context === NL_SEARCH_CAD_CONTEXT) {
    body.context = NL_SEARCH_CAD_CONTEXT;
  }

  return post('/parsesearchquery', {
    body,
    signal,
    timeoutMs: NL_SEARCH_TIMEOUT_MS,
  });
};
